import * as Sentry from '@sentry/react'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { type UserNoticeSurface, UserNoticeType } from '@/generated/graphql'
import { isOpenable } from '../action-url'
import type { UserNoticeSource } from '../analytics'
import { isMidTask, noticeSource, noticeSurface } from '../surfaces'
import { useUserNotice } from './use-user-notice'

// Module scope, because component state is not enough: `/plan/day/:id` is a sibling route
// OUTSIDE the protected layout, so opening a day unmounts the layout and resets its state —
// while the verdict stays in the query cache. A dismissed screen would come back on return,
// with a fresh screenView, every day the user opens a day. Keyed by user so a different
// session in the same tab starts clean.
let dismissedFor = ''
let dismissedSurfaces: ReadonlySet<UserNoticeSurface> = new Set()

function readDismissed(userId: string): ReadonlySet<UserNoticeSurface> {
  return dismissedFor === userId ? dismissedSurfaces : new Set()
}

export type UserNoticeDecision =
  // Asked, not answered yet. Nobody may act on this: treating it as clear is what lets a
  // report fire a beat before the screen that was about to cover it.
  | { status: 'unknown' }
  // Nothing will cover this surface — no verdict to show, dismissed, or never asked here.
  | { status: 'clear' }
  | {
      status: 'covered'
      noticeType: UserNoticeType
      actionUrl: string
      source: UserNoticeSource
      dismiss: () => void
    }

/**
 * The one answer to "is a payment-method screen about to cover this surface?".
 *
 * Read by the gate, which renders the screen, AND by the exposure report, which must not
 * fire behind it — iOS gets this for free because a staged notice keeps the main flow from
 * being entered at all (LaunchResolver -> MainTabCoordinator.observePlanEntries), so nothing
 * is behind the screen to report. On the web the layout is the surface, so the same
 * guarantee has to be an explicit decision that both callers read.
 *
 * Asking is what consumes the day's SOFT show, so this hook is the only asker: `enabled`
 * must stay false until there is a session AND routing has settled.
 */
export function useUserNoticeDecision(
  enabled: boolean,
  userId: string | undefined,
): UserNoticeDecision {
  const [dismissed, setDismissed] = useState<ReadonlySet<UserNoticeSurface>>(
    () => readDismissed(userId ?? ''),
  )
  const { pathname } = useLocation()
  const midTask = isMidTask(pathname)
  const surface = noticeSurface(pathname)
  const asking = enabled && !midTask
  const { data, isPending } = useUserNotice(asking, surface)

  const prompt = data?.userNotice
  const noticeType = prompt?.noticeType
  const isShowable =
    noticeType === UserNoticeType.Hard || noticeType === UserNoticeType.Soft
  const dropped = isShowable && !isOpenable(prompt?.actionUrl ?? '')

  // Loud, because nothing downstream can tell a dropped verdict from "this user had nothing
  // to show" — and a dropped HARD releases a gate that was meant to block. Reported from an
  // effect, not the render, so a re-render does not re-report the same drop; and never with
  // the URL itself, which carries the customer's email.
  useEffect(() => {
    if (!dropped) return
    Sentry.captureMessage('userNotice dropped: unopenable actionUrl', {
      level: 'error',
      tags: { noticeType, surface },
    })
  }, [dropped, noticeType, surface])

  // Nothing is asked mid-task, so nothing can cover it either — answering `unknown` here
  // would hold a report that has nothing to wait for.
  if (midTask) return { status: 'clear' }
  if (asking && isPending) return { status: 'unknown' }
  if (!prompt || !isShowable || dropped) return { status: 'clear' }
  // A dead-end cannot be dismissed; only SOFT honours the close.
  if (dismissed.has(surface) && noticeType !== UserNoticeType.Hard) {
    return { status: 'clear' }
  }

  return {
    status: 'covered',
    noticeType,
    actionUrl: prompt.actionUrl,
    source: noticeSource(pathname),
    dismiss: () => {
      const next = new Set(dismissed).add(surface)
      dismissedFor = userId ?? ''
      dismissedSurfaces = next
      setDismissed(next)
    },
  }
}
